import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Moon, Heart, Brain, Calendar, ArrowRight } from 'lucide-react';
import CTA from '../components/CTA';

const AnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety & Stress Hypnotherapy | Sleep, Calm & Clarity</title>
        <meta name="description" content="Ease stress, reduce anxiety, and restore restful sleep with gentle solution-focused hypnotherapy in Belfast, Lisburn & Northern Ireland." />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/anxiety-stress/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How many hypnotherapy sessions will I need for anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients see significant improvement in 4-6 sessions for anxiety. However, the number varies based on individual circumstances and goals. We'll discuss your specific needs during the free 10-minute consultation."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for anxiety. Research shows it can reduce anxiety symptoms by working directly with the subconscious mind to change thought patterns and responses. It helps calm the fight-or-flight response and creates new, healthier neural pathways."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with sleep problems caused by anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Hypnotherapy helps re-establish healthy sleep patterns by calming the mind before bed and replacing anxious thoughts with soothing, helpful ones. Many clients report improved sleep quality within just a few sessions."
                }
              },
              {
                "@type": "Question",
                "name": "What's the difference between hypnotherapy and counselling for anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy works directly with the subconscious mind to create rapid change in thought patterns and behaviors, while counselling typically involves talking through issues consciously. Hypnotherapy is solution-focused and often produces results more quickly for anxiety."
                }
              },
              {
                "@type": "Question",
                "name": "Is hypnotherapy safe for anxiety and stress?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is completely safe when practiced by a qualified clinical hypnotherapist. You remain in control throughout the session and cannot be made to do anything against your will. It's a natural, relaxing state similar to daydreaming."
                }
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Anxiety and Stress Hypnotherapy",
            "name": "Anxiety & Stress Relief Hypnotherapy",
            "description": "Professional solution-focused hypnotherapy to ease stress, reduce anxiety, and restore restful sleep. Serving Belfast, Lisburn and Northern Ireland.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Sharon Clare Hypnotherapy",
              "telephone": "07932139245",
              "email": "Sharon@sharonclare-hypnotherapy.com",
              "url": "https://www.sharonclare-hypnotherapy.com/",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "addressRegion": "County Antrim",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "areaServed": ["Belfast", "Lisburn", "Northern Ireland"],
            "availableChannel": {
              "@type": "ServiceChannel",
              "serviceUrl": "https://www.sharonclare-hypnotherapy.com/anxiety-stress",
              "servicePhone": {
                "@type": "ContactPoint",
                "telephone": "07932139245",
                "contactType": "customer service"
              }
            }
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">Anxiety and Stress</h1>
          <p className="text-xl font-heading text-gray-900 mb-3">Find Calm in the Chaos</p>
          <p className="text-lg sm:text-xl text-gray-600">Ease stress, reduce anxiety, and rediscover restful sleep. Specialized support also available for <a href="/medical-procedure-anxiety" className="text-accent-600 hover:text-accent-700 underline">medical anxiety</a> and <a href="/burnout-recovery" className="text-accent-600 hover:text-accent-700 underline">burnout recovery</a>.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/book" className="btn-primary inline-flex items-center justify-center">
              <Calendar className="w-5 h-5 mr-2" /> Request a 10-Minute Call
            </a>
            <a href="tel:07932139245" className="btn-secondary inline-flex items-center justify-center">
              <ArrowRight className="w-5 h-5 mr-2" /> Call 07932 139245
            </a>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">How Stress and Anxiety Affect You</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Do you feel like stress is always one step ahead? Maybe your mind races at night or anxiety shows up with a pounding heart, tense shoulders, or a constant sense of unease. Over time, stress and anxiety can impact your sleep, energy, relationships, and quality of life.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <ul className="space-y-3">
                {[
                  'Difficulty falling or staying asleep',
                  'Feeling overwhelmed by everyday tasks',
                  'Trouble concentrating or making decisions',
                  'Physical symptoms like headaches, tight muscles, or upset stomachs',
                  'Avoiding situations out of fear or worry',
                  'Constantly feeling on edge or unable to relax',
                ].map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-6">
              <p className="text-gray-700">
                When stress and anxiety take over, it can feel impossible to switch off. But there is hope—you can break the cycle and regain control.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How hypnotherapy helps */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">How Hypnotherapy Can Help</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Solution‑Focused Hypnotherapy gently shifts focus away from worry and toward practical, positive change. Hypnosis calms the overactive fight‑or‑flight response so panic and tension reduce, and clarity returns.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: <Heart className="w-6 h-6 text-accent-600" />, title: 'Calm the system', text: 'Soothe the stress response and reduce physical anxiety symptoms.' },
              { icon: <Brain className="w-6 h-6 text-accent-600" />, title: 'Rewire patterns', text: 'Build new habits of thought and behaviour that support resilience.' },
              { icon: <Moon className="w-6 h-6 text-accent-600" />, title: 'Restore sleep', text: 'Create a calmer pre‑sleep routine and wake feeling more refreshed.' },
            ].map(({ icon, title, text }) => (
              <div key={title} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-center space-x-3 mb-2">
                  {icon}
                  <h3 className="font-heading font-semibold text-gray-900">{title}</h3>
                </div>
                <p className="text-gray-700 text-sm">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What it helps */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">What Hypnotherapy Can Help With</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <ul className="space-y-3">
                {[
                  'Generalised anxiety',
                  'Social anxiety',
                  'Work‑related stress',
                  'Exam or performance stress',
                  'Panic attacks',
                  'Sleep issues',
                ].map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-6">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Why Sleep Matters</h3>
              <p className="text-gray-700">Hypnotherapy helps re‑establish a healthy sleep cycle by calming the mind before bed and replacing anxious thoughts with soothing, helpful ones.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-gray-900 mb-6">
              Accessible Across Northern Ireland
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Book anxiety and stress relief sessions at any of our three convenient locations:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <a href="/belfast-hypnotherapy" className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-gray-900 mb-2">Belfast</h3>
                <p className="text-sm text-gray-600 mb-1">Days &amp; times vary — early evening available</p>
                <p className="text-xs text-gray-500">Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ</p>
              </a>
              <a href="/lisburn-hypnotherapy" className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-gray-900 mb-2">Lisburn</h3>
                <p className="text-sm text-gray-600 mb-1">Times vary — early evening available</p>
                <p className="text-xs text-gray-500">The Arzen Collective, 13B Antrim Street, Lisburn BT28</p>
              </a>
              <a href="/contact" className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-gray-900 mb-2">Online</h3>
                <p className="text-sm text-gray-600 mb-1">Flexible Scheduling</p>
                <p className="text-xs text-gray-500">Across Northern Ireland & UK</p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Your Path to Calm Starts Here"
        subtitle="Take the first step toward less stress, less worry, and more peace. Book a consultation today and start your journey toward calm, clarity, and better sleep."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default AnxietyStress;

